import { createEntityAdapter, createSelector } from "@reduxjs/toolkit";

import {
  AccountInvitation,
  EntityType,
  ExternalAccount,
  GetUsersResponse,
  OwnUser,
  Store,
  User,
  UserAccountAllocationResponse,
  UserMentionSuggestion,
  UserPreference,
  UserPreferenceKey,
  UserPreferenceSubKey,
  UserShareSuggestion,
} from "fond/types";

import { apiSlice } from "./apiSlice";

export type GetUserPreferencesResponse = {
  Items: UserPreference[];
};

export type GetUserAccountsResponse = {
  Allocations: UserAccountAllocationResponse[];
};

type GetUserShareSuggestionsResponse = {
  Suggestions: UserShareSuggestion[];
};

type GetUserMentionSuggestionsResponse = {
  Suggestions: UserMentionSuggestion[];
};

type GetExternalAccountsResponse = {
  GuestAccounts: ExternalAccount[];
};

type Subscriber = {
  ID: string;
  Hash: string;
};

type NotificationSubscriber = {
  Subscriber: Subscriber;
};

type UserShareSuggestionArgs = {
  scenario: `${EntityType}.share`;
  entityId: string;
};

const preferencesAdaptor = createEntityAdapter<UserPreference>({
  selectId: (entity: UserPreference): string => entity.ID,
});
const preferencesInitialState = preferencesAdaptor.getInitialState();

/**
 * User API Slice
 */
export const userSlice = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    getUserPendingInvitations: build.query<AccountInvitation[], void>({
      query: () => "/v2/users/me/invitations",
      transformResponse: (response: { Invitations: AccountInvitation[] }) => response.Invitations,
    }),
    getUserMe: build.query<OwnUser | undefined, void>({
      query: () => `/v2/users/me`,
      transformResponse: (response: OwnUser) => response,
      providesTags: (result) => (result ? [{ type: "User", id: result.ID }] : []),
    }),
    getNotificationSubscriber: build.query<NotificationSubscriber, void>({
      query: () => "/v2/notification-subscriber",
    }),
    getUserMeAccounts: build.query<UserAccountAllocationResponse[], void>({
      query: () => `/v2/users/me/accounts`,
      transformResponse: (response: GetUserAccountsResponse) => response?.Allocations || [],
      providesTags: (result) => (result ? result.map((allocation) => ({ type: "UserAccountAllocation" as const, id: allocation.ID })) : []),
    }),
    getUserByEmail: build.query<User | undefined, string>({
      query: (email: string) => ({
        url: "/v2/users",
        params: { email },
      }),
      transformResponse: (response: GetUsersResponse) => {
        return response.Users?.[0] || undefined;
      },
      providesTags: (result) => (result ? [{ type: "User", id: result.ID }] : []),
    }),
    getUserPreferences: build.query({
      query: () => `/v2/user-preferences`,
      transformResponse: (response: GetUserPreferencesResponse) => preferencesAdaptor.setAll(preferencesInitialState, response.Items),
      providesTags: (result) =>
        result
          ? [
              ...Object.values(result.entities).map((entity) => ({ type: "UserPreference" as const, id: `${entity?.Key}_${entity?.Subkey}` })),
              { type: "UserPreference", id: "LIST" },
            ]
          : [{ type: "UserPreference", id: "LIST" }],
    }),
    updateUserPreferences: build.mutation<UserPreference, Pick<UserPreference, "Key" | "Subkey" | "Value">>({
      query: ({ Key, Subkey, Value }) => ({
        url: `/v2/user-preferences`,
        method: "POST",
        body: {
          Key,
          Subkey,
          Value,
        },
      }),
      invalidatesTags: (result, error, arg) => [{ type: "UserPreference" as const, id: `${result?.Key}_${result?.Subkey}` }],
    }),
    getUserShareSuggestions: build.query<UserShareSuggestion[], UserShareSuggestionArgs>({
      query: ({ scenario, entityId }) => {
        const resourceType = scenario.split(".")[0] as EntityType;
        return {
          url: "/v2/suggestions",
          params: {
            scenario,
            [`${resourceType}_id`]: entityId,
          },
        };
      },
      transformResponse: (response: GetUserShareSuggestionsResponse) => response.Suggestions || [],
      providesTags: (result, _error, arg) => (result ? [{ type: "ShareSuggestions", id: arg.entityId }] : []),
    }),
    getUserMentionSuggestions: build.query<UserMentionSuggestion[], { projectId: string }>({
      query: ({ projectId }) => ({
        url: "/v2/suggestions",
        params: {
          scenario: "project.comment-mention",
          project_id: projectId,
        },
      }),
      transformResponse: (response: GetUserMentionSuggestionsResponse) => response.Suggestions || [],
      providesTags: (result, _error, arg) => (result ? [{ type: "MentionSuggestions", id: arg.projectId }] : []),
    }),
    getExternalAccounts: build.query<GetExternalAccountsResponse["GuestAccounts"], void>({
      query: () => `/v2/users/me/guest-accounts`,
      transformResponse: (response: GetExternalAccountsResponse) => response?.GuestAccounts || [],
    }),
  }),
});

/**
 * Endpoint Hooks
 */
export const {
  useGetUserPendingInvitationsQuery,
  useGetUserMeQuery,
  useGetNotificationSubscriberQuery,
  useGetUserMeAccountsQuery,
  useGetUserByEmailQuery,
  useGetUserPreferencesQuery,
  useUpdateUserPreferencesMutation,
  useGetUserShareSuggestionsQuery,
  useGetUserMentionSuggestionsQuery,
  useGetExternalAccountsQuery,
} = userSlice;

/**
 * Selectors
 */
const selectPreferencesResult = userSlice.endpoints.getUserPreferences.select(undefined);
const selectPreferencesData = createSelector(selectPreferencesResult, (preferencesResult) => preferencesResult.data);

export const { selectAll: selectAllPreferences, selectById: selectPreferenceById } = preferencesAdaptor.getSelectors(
  (state: Store) => selectPreferencesData(state) ?? preferencesInitialState
);

export const getUserPreferenceValue = createSelector(
  [
    selectAllPreferences,
    (state: Store, key: UserPreferenceKey) => key,
    (state: Store, key: UserPreferenceKey, subKey: UserPreferenceSubKey | string) => subKey,
  ],
  (preferences: UserPreference[], key: UserPreferenceKey, subKey: UserPreferenceSubKey) => {
    return preferences.find((preference) => preference.Key === key && preference.Subkey === subKey)?.Value;
  }
);

export const selectUserMeAccountsResult = userSlice.endpoints.getUserMeAccounts.select();

const selectUserMeResult = userSlice.endpoints.getUserMe.select();
export const selectUserId = createSelector(selectUserMeResult, (userResult) => userResult.data?.ID);
export const selectUserModules = createSelector(selectUserMeResult, (userResult) => userResult.data?.Modules ?? []);
export const selectUsername = createSelector(selectUserMeResult, (userResult) => userResult.data?.Email);

/**
 * Select external accounts from the API.
 * External accounts are accounts where the user has access to shared items but is not a member.
 */
const selectExternalAccountsResult = userSlice.endpoints.getExternalAccounts.select();
export const selectExternalAccounts = createSelector(selectExternalAccountsResult, (externalAccountsResult) => externalAccountsResult.data ?? []);

/**
 * Memoized selector to find a specific external account by ID.
 * Returns an ExternalAccount object (with Logo/colors) or undefined if not found.
 */
export const selectExternalAccountById = createSelector(
  [selectExternalAccounts, (_state: Store, accountId: string | undefined) => accountId],
  (externalAccounts, accountId): ExternalAccount | undefined => {
    if (!accountId) return undefined;
    return externalAccounts.find((account) => account.ID === accountId);
  }
);
